import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Slider } from "../../components/ui/slider";
import ModuleGate from "../../components/ModuleGate";
import { 
  ArrowLeft,
  Scale,
  Camera,
  Activity,
  Moon,
  Utensils,
  Brain,
  CheckCircle2,
  Dumbbell,
  ChevronDown,
  ChevronUp,
  Ruler,
  Save,
  Loader2,
  Sparkles,
  X
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Slider component with label
const MetricSlider = ({ label, icon: Icon, value, onChange, color, min = 1, max = 10, description }) => {
  const getValueLabel = (val) => {
    if (val <= 3) return "Bajo";
    if (val <= 6) return "Medio";
    return "Alto";
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={cn("w-4 h-4", color)} />
          <span className="text-white text-sm font-medium">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn("text-lg font-bold", color)}>{value || '-'}</span>
          <span className="text-[#666] text-xs">/ {max}</span>
        </div>
      </div>
      <Slider
        value={[value || 5]}
        onValueChange={(val) => onChange(val[0])}
        min={min}
        max={max}
        step={1}
        className="py-2"
      />
      <div className="flex justify-between text-[10px] text-[#555]">
        <span>{min}</span>
        <span className={cn("font-medium", color)}>{value ? getValueLabel(value) : ''}</span>
        <span>{max}</span>
      </div>
      {description && <p className="text-[#555] text-[10px]">{description}</p>}
    </div>
  );
};

// Photo upload component with enhanced compression for mobile
const PhotoUpload = ({ label, value, onChange, icon: Icon }) => {
  const [preview, setPreview] = useState(value);
  const [uploading, setUploading] = useState(false);
  
  // Enhanced compression for mobile - optimized for ~100KB output
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          
          // Max dimension 600px for mobile optimization
          const maxSize = 600;
          let { width, height } = img;
          
          // Calculate aspect ratio and resize
          if (width > height) {
            if (width > maxSize) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          // Use higher quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Start with 0.6 quality, decrease if still too large
          let quality = 0.6;
          let compressed = canvas.toDataURL('image/jpeg', quality);
          
          // If still > 200KB, reduce quality
          while (compressed.length > 200000 && quality > 0.3) {
            quality -= 0.1;
            compressed = canvas.toDataURL('image/jpeg', quality);
          }
          
          resolve(compressed);
        };
        img.onerror = () => reject(new Error('Error cargando imagen'));
        img.src = event.target.result;
      };
      reader.onerror = () => reject(new Error('Error leyendo archivo'));
      reader.readAsDataURL(file);
    });
  };
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }
    
    // Validate initial size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen es demasiado grande (máx. 10MB)');
      return;
    }
    
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      setPreview(compressed);
      onChange(compressed);
      
      // Size tracking removed for production
    } catch (error) {
      toast.error('Error procesando imagen');
    } finally {
      setUploading(false);
    }
  };
  
  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview(null);
    onChange(null);
  };

  return (
    <div className="space-y-2">
      <label className="text-[#888] text-xs flex items-center gap-2">
        <Icon className="w-3 h-3" />
        {label}
      </label>
      <div 
        className={cn(
          "relative aspect-[3/4] rounded-lg border-2 border-dashed overflow-hidden cursor-pointer transition-colors",
          preview ? "border-[#D4AF37]/30" : "border-[#2A2A2A] hover:border-[#3A3A3A]"
        )}
        onClick={() => !uploading && document.getElementById(`photo-${label}`)?.click()}
      >
        {uploading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1A1A1A]">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mb-2" />
            <span className="text-[#888] text-xs">Comprimiendo...</span>
          </div>
        ) : preview ? (
          <>
            <img src={preview} alt={label} className="w-full h-full object-cover" />
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors"
            >
              <X className="w-3 h-3 text-white" />
            </button>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
              <span className="text-white text-[10px] font-medium">{label}</span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[#555]">
            <Camera className="w-8 h-8 mb-2" />
            <span className="text-xs">Subir foto</span>
            <span className="text-[10px] text-[#444] mt-1">Toca para añadir</span>
          </div>
        )}
        <input
          id={`photo-${label}`}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

// Main Check-in Page
const CheckInPage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingCheckIn, setExistingCheckIn] = useState(null);
  const [showMeasurements, setShowMeasurements] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    weight: '',
    photos: { front: null, side: null, back: null },
    measurements: { waist: '', hips: '', chest: '', arm: '', leg: '' },
    energy_level: 5,
    hunger_level: 5,
    sleep_quality: 5,
    stress_level: 5,
    diet_adherence: 80,
    training_adherence: 80,
    digestion_notes: '',
    client_notes: ''
  });

  // Load existing check-in for this week
  const loadCurrentWeek = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/premium/check-ins/current-week`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data.check_in) {
        const ci = response.data.check_in;
        setExistingCheckIn(ci);
        setFormData({
          weight: ci.weight || '',
          photos: ci.photos || { front: null, side: null, back: null },
          measurements: ci.measurements || { waist: '', hips: '', chest: '', arm: '', leg: '' },
          energy_level: ci.energy_level || 5,
          hunger_level: ci.hunger_level || 5,
          sleep_quality: ci.sleep_quality || 5,
          stress_level: ci.stress_level || 5,
          diet_adherence: ci.diet_adherence || 80,
          training_adherence: ci.training_adherence || 80,
          digestion_notes: ci.digestion_notes || '',
          client_notes: ci.client_notes || ''
        });
        // Show measurements if any exist
        if (Object.values(ci.measurements || {}).some(v => v)) {
          setShowMeasurements(true);
        }
      }
    } catch (error) {
      console.error("Error loading check-in:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadCurrentWeek();
  }, [loadCurrentWeek]);

  const handleSubmit = async () => {
    if (!formData.weight) {
      toast.error("El peso es obligatorio");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/premium/check-ins`,
        {
          weight: parseFloat(formData.weight),
          photos: formData.photos,
          measurements: {
            waist: formData.measurements.waist ? parseFloat(formData.measurements.waist) : null,
            hips: formData.measurements.hips ? parseFloat(formData.measurements.hips) : null,
            chest: formData.measurements.chest ? parseFloat(formData.measurements.chest) : null,
            arm: formData.measurements.arm ? parseFloat(formData.measurements.arm) : null,
            leg: formData.measurements.leg ? parseFloat(formData.measurements.leg) : null
          },
          energy_level: formData.energy_level,
          hunger_level: formData.hunger_level,
          sleep_quality: formData.sleep_quality,
          stress_level: formData.stress_level,
          diet_adherence: formData.diet_adherence,
          training_adherence: formData.training_adherence,
          digestion_notes: formData.digestion_notes,
          client_notes: formData.client_notes
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success(existingCheckIn ? "Check-in actualizado" : "Check-in enviado");
      navigate(-1);
    } catch (error) {
      toast.error("Error al guardar check-in");
    } finally {
      setSaving(false);
    }
  };

  const weekNumber = new Date().toLocaleDateString('es-ES', { week: 'numeric' });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <ModuleGate moduleId="checkins">
    <div className="min-h-screen bg-[#0B0B0B] pb-24" data-testid="check-in-page">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0B]/95 backdrop-blur-sm border-b border-[#2A2A2A] p-4 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-3 -ml-3 text-white min-w-[44px] min-h-[44px] flex items-center justify-center">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-bold">Check-in Semanal</h1>
            <p className="text-[#666] text-xs">Semana {new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</p>
          </div>
          {existingCheckIn && (
            <span className="px-2 py-1 text-[10px] bg-green-500/10 text-green-400 rounded">
              Actualizar
            </span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Weight (Required) */}
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
              <Scale className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="text-white font-semibold">Peso corporal</h2>
              <p className="text-[#666] text-xs">En ayunas, después de ir al baño</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.1"
              value={formData.weight}
              onChange={(e) => setFormData({...formData, weight: e.target.value})}
              placeholder="75.5"
              className="text-2xl font-bold bg-[#0B0B0B] border-[#2A2A2A] text-[#D4AF37] text-center"
            />
            <span className="text-[#666] text-lg">kg</span>
          </div>
        </div>

        {/* Photos */}
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Camera className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold">Fotos de progreso</h2>
              <p className="text-[#666] text-xs">Misma pose y luz cada semana</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <PhotoUpload
              label="Frontal"
              icon={Camera}
              value={formData.photos.front}
              onChange={(val) => setFormData({...formData, photos: {...formData.photos, front: val}})}
            />
            <PhotoUpload
              label="Lateral"
              icon={Camera}
              value={formData.photos.side}
              onChange={(val) => setFormData({...formData, photos: {...formData.photos, side: val}})}
            />
            <PhotoUpload
              label="Espalda"
              icon={Camera}
              value={formData.photos.back}
              onChange={(val) => setFormData({...formData, photos: {...formData.photos, back: val}})}
            />
          </div>
        </div>

        {/* Measurements (Collapsible) */}
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
          <button
            onClick={() => setShowMeasurements(!showMeasurements)}
            className="flex items-center justify-between w-full"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                <Ruler className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-left">
                <h2 className="text-white font-semibold">Medidas corporales</h2>
                <p className="text-[#666] text-xs">Opcional - en cm</p>
              </div>
            </div>
            {showMeasurements ? <ChevronUp className="w-5 h-5 text-[#666]" /> : <ChevronDown className="w-5 h-5 text-[#666]" />}
          </button>
          
          {showMeasurements && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4 pt-4 border-t border-[#2A2A2A]">
              {[
                { key: 'waist', label: 'Cintura' },
                { key: 'hips', label: 'Cadera' },
                { key: 'chest', label: 'Pecho' },
                { key: 'arm', label: 'Brazo' },
                { key: 'leg', label: 'Pierna' }
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="text-[#666] text-[10px] block mb-1">{label}</label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.measurements[key]}
                      onChange={(e) => setFormData({
                        ...formData, 
                        measurements: {...formData.measurements, [key]: e.target.value}
                      })}
                      className="bg-[#0B0B0B] border-[#2A2A2A] text-white text-sm"
                      placeholder="--"
                    />
                    <span className="text-[#555] text-[10px]">cm</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Wellness Metrics */}
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Activity className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold">Sensaciones</h2>
              <p className="text-[#666] text-xs">¿Cómo te has sentido esta semana?</p>
            </div>
          </div>

          <MetricSlider
            label="Nivel de energía"
            icon={Activity}
            color="text-green-400"
            value={formData.energy_level}
            onChange={(val) => setFormData({...formData, energy_level: val})}
            description="1 = Agotado, 10 = Lleno de energía"
          />

          <MetricSlider
            label="Hambre / Apetito"
            icon={Utensils}
            color="text-orange-400"
            value={formData.hunger_level}
            onChange={(val) => setFormData({...formData, hunger_level: val})}
            description="1 = Sin hambre, 10 = Hambre extrema"
          />

          <MetricSlider
            label="Calidad del sueño"
            icon={Moon}
            color="text-blue-400"
            value={formData.sleep_quality}
            onChange={(val) => setFormData({...formData, sleep_quality: val})}
            description="1 = Muy mal, 10 = Perfecto"
          />

          <MetricSlider
            label="Nivel de estrés"
            icon={Brain}
            color="text-red-400"
            value={formData.stress_level}
            onChange={(val) => setFormData({...formData, stress_level: val})}
            description="1 = Muy relajado, 10 = Muy estresado"
          />
        </div>

        {/* Adherence */}
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="text-white font-semibold">Adherencia</h2>
              <p className="text-[#666] text-xs">¿Cuánto has seguido el plan?</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Utensils className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-white text-sm">Dieta</span>
              </div>
              <span className="text-[#D4AF37] font-bold">{formData.diet_adherence}%</span>
            </div>
            <Slider
              value={[formData.diet_adherence]}
              onValueChange={(val) => setFormData({...formData, diet_adherence: val[0]})}
              min={0}
              max={100}
              step={5}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Dumbbell className="w-4 h-4 text-purple-400" />
                <span className="text-white text-sm">Entreno</span>
              </div>
              <span className="text-purple-400 font-bold">{formData.training_adherence}%</span>
            </div>
            <Slider
              value={[formData.training_adherence]}
              onValueChange={(val) => setFormData({...formData, training_adherence: val[0]})}
              min={0}
              max={100}
              step={5}
            />
          </div>
        </div>

        {/* Notes */}
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] space-y-4">
          <div>
            <label className="text-[#888] text-xs block mb-2">Digestión / Molestias</label>
            <Textarea
              value={formData.digestion_notes}
              onChange={(e) => setFormData({...formData, digestion_notes: e.target.value})}
              placeholder="¿Algún problema digestivo, hinchazón, molestias...?"
              className="bg-[#0B0B0B] border-[#2A2A2A] text-white min-h-[60px]"
            />
          </div>
          
          <div>
            <label className="text-[#888] text-xs block mb-2">Observaciones adicionales</label>
            <Textarea
              value={formData.client_notes}
              onChange={(e) => setFormData({...formData, client_notes: e.target.value})}
              placeholder="Cualquier cosa que quieras comentar a tu coach..."
              className="bg-[#0B0B0B] border-[#2A2A2A] text-white min-h-[80px]"
            />
          </div>
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={saving || !formData.weight}
          className="w-full h-14 bg-[#D4AF37] hover:bg-[#B8962E] text-[#0B0B0B] font-bold text-lg"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              {existingCheckIn ? "Actualizar Check-in" : "Enviar Check-in"}
            </>
          )}
        </Button>

        {/* Info */}
        <p className="text-[#555] text-xs text-center">
          Tu coach recibirá este check-in y lo revisará pronto.
        </p>
      </div>
    </div>
    </ModuleGate>
  );
};

export default CheckInPage;
